import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isSwapping = false; // Protection against recursion

  #sellingPlansByVariant = {
    "47073073266937": { 1: 5539463417, 2: 5539496185, 3: 5539528953 },
    "47073073299705": { 1: 5542084857, 2: 5542117625, 3: 5542150393 },
    "47073073332473": { 1: 5542183161, 2: 5542215929, 3: 5542248697 },
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Helper method for grammar (jar/jars)
   */
  #getCorrectPlanName(quantity, currentName) {
    if (!currentName) return null;

    // Extract the part after digits and dash (e.g. "jars--promo")
    let base = currentName.replace(/^\d+-/, '');

    // Fix jar/jars at the beginning of the base, preserving suffixes
    if (quantity === 1) {
      base = base.replace(/^jars/, 'jar');
    } else {
      console.log('base.startsWith(\'jar\')', base.startsWith('jar'));

      if (base.startsWith('jar') && !base.startsWith('jars')) {
        base = base.replace(/^jar/, 'jars');
      }
    }

    return `${quantity}-${base}`;
  }

  /**
   * UI quantity change handler
   */
  async #onQuantityChange(event) {
    if (this.#isSwapping) return;

    const { quantity, cartLine: line, variantId, properties } = event.detail;

    if (!line) return;
    if (quantity === 0) return this.onLineItemRemove(line);

    const variantData = this.#sellingPlansByVariant[variantId?.toString()];
    const sellingPlanId = variantData ? variantData[quantity] : null;

    if (sellingPlanId && properties?._selling_plan_name) {
      const updatedPlanName = this.#getCorrectPlanName(quantity, properties._selling_plan_name);

      await this.swapItemWithFullData({
        line,
        variantId,
        quantity,
        sellingPlanId,
        properties: { ...properties, _selling_plan_name: updatedPlanName }
      });
    } else {
      await this.updateQuantity({ line, quantity, action: 'change' });
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow) {
      const textComponent = lineItemRow.querySelector('text-component');
      textComponent?.shimmer();
    }
  }

  /**
   * Global cart update handler
   */
  #handleCartUpdate = async (event) => {
    // 1. Protection against recursion
    if (this.#isSwapping) return;

    // 2. Ignore our own swap events
    if (event.detail?.source === 'cart-items-swap-action' ||
      event.detail?.data?.source === 'cart-items-swap-action') return;

    // 3. Handle discounts
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    // 4. Get product data
    // If the event doesn't have items array, make a request to get current cart state
    let cartData = event.detail?.data || event.detail?.cart || event.detail;

    if (!cartData.items) {
      try {
        const response = await fetch(`${Theme.routes.cart_url}.js`);
        cartData = await response.json();
      } catch (e) {
        console.error("Failed to fetch cart data for swap check", e);
        return;
      }
    }

    // 5. Check if "Swap" (replacement) is needed
    // for (let i = 0; i < cartData.items.length; i++) {
    //   const item = cartData.items[i];
    //   const variantId = item.variant_id.toString();
    //   const quantity = item.quantity;
    //
    //   const targetPlanId = this.#sellingPlansByVariant[variantId]?.[quantity];
    //   const currentPlanId = item.selling_plan_allocation?.selling_plan?.id;
    //   const currentPlanNameProp = item.properties?.['_selling_plan_name'];
    //   const correctPlanName = this.#getCorrectPlanName(quantity, currentPlanNameProp);
    //
    //   if (targetPlanId && (targetPlanId !== currentPlanId || currentPlanNameProp !== correctPlanName)) {
    //     console.log(`[Swap Required] Item ${variantId}: switching to plan ${targetPlanId}`);
    //     await this.swapItemWithFullData({
    //       line: i + 1,
    //       variantId,
    //       quantity,
    //       sellingPlanId: targetPlanId,
    //       properties: {
    //         ...item.properties,
    //         _selling_plan_name: correctPlanName
    //       }
    //     });
    //     return; // Stop execution, swapItem will create a new dispatch
    //   }
    // }

    // 6. If swap is not needed — update DOM using morphSection
    const sections = event.detail?.data?.sections || event.detail?.sections;
    const cartItemsHtml = sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      // If there's no HTML in the event, re-render section manually
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Method for item swap
   */
  async swapItemWithFullData({ line, variantId, quantity, sellingPlanId, properties }) {
    if (this.#isSwapping) return;

    this.#isSwapping = true;
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    try {
      // 1. Remove old line
      await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
        body: JSON.stringify({ line, quantity: 0 })
      }));

      // Short pause for server session processing
      await new Promise(r => setTimeout(r, 100));

      // 2. Add new with correct plan
      const sectionsToUpdate = this.#getSectionsToUpdate();
      const response = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          selling_plan: sellingPlanId,
          properties: properties,
          sections: sectionsToUpdate,
          sections_url: window.location.pathname
        })
      }));

      const parsedResponse = await response.json();

      // Notify theme about update with swap label
      this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
        itemCount: parsedResponse.item_count,
        source: 'cart-items-swap-action',
        sections: parsedResponse.sections,
        data: parsedResponse
      }));

      this.#processCartUpdate(parsedResponse);
    } catch (error) {
      console.error('Swap Error:', error);
    } finally {
      // Unlock component with small delay
      setTimeout(() => {
        this.#isSwapping = false;
        this.#enableCartItems();
      }, 300);
    }
  }

  async updateQuantity(config) {
    const cartMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    try {
      const { line, quantity } = config;
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
        body: JSON.stringify({
          line,
          quantity,
          sections: this.#getSectionsToUpdate(),
          sections_url: window.location.pathname
        })
      }));

      const parsedResponse = await response.json();
      if (parsedResponse.errors) {
        this.#handleCartError(line, parsedResponse);
        return;
      }
      this.#processCartUpdate(parsedResponse);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartMarker);
    }
  }

  #processCartUpdate(parsedResponse) {
    resetShimmer(this);
    const sectionHtml = parsedResponse.sections?.[this.sectionId];
    if (!sectionHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newCount = doc.querySelector('[ref="cartItemCount"]')?.textContent;

    this.#updateQuantitySelectors(parsedResponse);

    this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
      itemCount: newCount ? parseInt(newCount, 10) : 0,
      source: 'cart-items-component',
      sections: parsedResponse.sections,
    }));

    morphSection(this.sectionId, sectionHtml);
    this.#updateCartQuantitySelectorButtonStates();
  }

  #getSectionsToUpdate() {
    const sections = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });
    return Array.from(sections).join(',');
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;
    const remove = () => row.remove();
    if (prefersReducedMotion()) return remove();
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    onAnimationEnd(row, remove);
  }

  handleDiscountUpdate = (event) => this.#handleCartUpdate(event);

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const input = quantitySelector?.querySelector('input');
    if (input) input.value = input.defaultValue;
    const container = this.refs[`cartItemErrorContainer-${line}`];
    const errorMessage = this.refs[`cartItemError-${line}`];
    if (errorMessage && container) {
      errorMessage.textContent = parsedResponseText.errors;
      container.classList.remove('hidden');
    }
  };

  #disableCartItems() { this.classList.add('cart-items-disabled'); }
  #enableCartItems() { this.classList.remove('cart-items-disabled'); }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    updatedCart.items.forEach(item => {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`[data-variant-id="${variantId}"]`);
      selectors.forEach(s => {
        const input = s.querySelector('input[data-cart-quantity]');
        if (input) input.setAttribute('data-cart-quantity', item.quantity.toString());
        if (typeof s.updateCartQuantity === 'function') s.updateCartQuantity();
      });
    });
  }

  #updateCartQuantitySelectorButtonStates() {
    document.querySelectorAll('cart-quantity-selector-component').forEach(s => {
      if (typeof s.updateButtonStates === 'function') s.updateButtonStates();
    });
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}